import React, {useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {PageTitle} from '../../../_metronic/layout/core'
import {KTIcon} from '../../../_metronic/helpers'
import {MovimientosTable} from './components/MovimientosTable'
import {EditarPagoModal} from './components/EditarPagoModal'
import {ConfirmarEliminarPagoModal} from './components/ConfirmarEliminarPagoModal'
import {Movimiento, ResumenMovimientos, GetMovimientosResponse} from './core/models'
import {fetchWithAuth} from '../../core/utils/fetchWithAuth'
import type {PagoParaEditar} from './components/EditarPagoModal'

const MovimientosPage: React.FC = () => {
  const navigate = useNavigate()
  
  // Estados de filtros
  const [fechaInicio, setFechaInicio] = useState<string>('')
  const [fechaFin, setFechaFin] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [debouncedQuery, setDebouncedQuery] = useState<string>('')
  
  // Estados de datos
  const [movimientos, setMovimientos] = useState<Movimiento[]>([])
  const [resumen, setResumen] = useState<ResumenMovimientos | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  
  // Paginación
  const [page, setPage] = useState<number>(1)
  const [total, setTotal] = useState<number>(0)
  const [totalPages, setTotalPages] = useState<number>(1)

  // Modales editar / eliminar pago
  const [pagoParaEditar, setPagoParaEditar] = useState<PagoParaEditar | null>(null)
  const [idPagoParaEliminar, setIdPagoParaEliminar] = useState<string | null>(null)

  const getToken = (): string | null => localStorage.getItem('X-TOKEN')

  const handleRefreshMovimientos = () => {
    fetchMovimientos()
  }

  // Debounce búsqueda
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery)
      setPage(1) // Resetear a página 1 al buscar
    }, 500)
    return () => clearTimeout(handler)
  }, [searchQuery])

  const fetchMovimientos = async () => {
    try {
      setLoading(true)
      setError(null)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'getMovimientos',
          idCuenta: '', // Opcional según requerimiento
          fechaInicio: fechaInicio,
          fechaFin: fechaFin,
          current_page: page,
          q: debouncedQuery,
        }),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data: GetMovimientosResponse = await response.json()

      if (data.code === 'OK') {
        setMovimientos(data.data.movimientos || [])
        setResumen(data.data.resumen)
        setTotal(data.data.total)
        setTotalPages(data.data.totalPages)
      } else if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
      } else {
        throw new Error(data.returned_message || 'Error al cargar movimientos')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  // Cargar datos cuando cambian los filtros o la página
  useEffect(() => {
    fetchMovimientos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedQuery, fechaInicio, fechaFin])

  return (
    <>
      <PageTitle>Movimientos</PageTitle>

      {/* Resumen Cards */}
      {resumen && (
        <div className='row g-5 g-xl-8 mb-8'>
          <div className='col-xl-4'>
            <div className='card card-flush border border-primary h-100'>
              <div className='card-body py-4 px-5 d-flex align-items-center'>
                <div className='symbol symbol-50px me-5'>
                  <span className='symbol-label bg-light-danger'>
                    <KTIcon iconName='arrow-down' className='fs-1 text-danger' />
                  </span>
                </div>
                <div className='d-flex flex-column'>
                  <span className='text-gray-500 fw-semibold fs-7 mb-1'>Total Egresos (DEBE)</span>
                  <span className='text-gray-800 fw-bold fs-2'>
                    $ {resumen.totalEgresos.toLocaleString('es-UY')}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className='col-xl-4'>
            <div className='card card-flush border border-success h-100'>
              <div className='card-body py-4 px-5 d-flex align-items-center'>
                <div className='symbol symbol-50px me-5'>
                  <span className='symbol-label bg-light-success'>
                    <KTIcon iconName='arrow-up' className='fs-1 text-success' />
                  </span>
                </div>
                <div className='d-flex flex-column'>
                  <span className='text-gray-500 fw-semibold fs-7 mb-1'>Total Ingresos (HABER)</span>
                  <span className='text-gray-800 fw-bold fs-2'>
                    $ {resumen.totalIngresos.toLocaleString('es-UY')}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className='col-xl-4'>
            <div className='card card-flush border border-warning h-100'>
              <div className='card-body py-4 px-5 d-flex align-items-center'>
                <div className='symbol symbol-50px me-5'>
                  <span className='symbol-label bg-light-warning'>
                    <KTIcon iconName='wallet' className='fs-1 text-warning' />
                  </span>
                </div>
                <div className='d-flex flex-column'>
                  <span className='text-gray-500 fw-semibold fs-7 mb-1'>Balance</span>
                  <span className={`fw-bold fs-2 text-${resumen.balance >= 0 ? 'success' : 'danger'}`}>
                    $ {resumen.balance.toLocaleString('es-UY')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className='row g-5 g-xl-8'>
        <div className='col-xl-12'>
          {/* Filtros y Header */}
          <div className='card mb-5 pb-5'>
            <div className='card-header border-0 pt-6 align-items-center'>
              <div className='card-title'>
                <div className='d-flex align-items-center position-relative my-1'>
                  <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
                  <input
                    type='text'
                    className='form-control form-control-solid w-250px ps-14'
                    placeholder='Buscar movimiento...'
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className='card-toolbar'>
                <div className='d-flex justify-content-end gap-3 align-items-center'>
                  <div className='d-flex align-items-center position-relative'>
                    <KTIcon iconName='calendar' className='fs-2 position-absolute ms-4' />
                    <input
                      type='date'
                      className='form-control form-control-solid ps-12'
                      placeholder='Fecha Inicio'
                      value={fechaInicio}
                      onChange={(e) => {
                        setFechaInicio(e.target.value)
                        setPage(1)
                      }}
                    />
                  </div>
                  <div className='d-flex align-items-center position-relative'>
                    <KTIcon iconName='calendar' className='fs-2 position-absolute ms-4' />
                    <input
                      type='date'
                      className='form-control form-control-solid ps-12'
                      placeholder='Fecha Fin'
                      value={fechaFin}
                      onChange={(e) => {
                        setFechaFin(e.target.value)
                        setPage(1)
                      }}
                    />
                  </div>
                  {(fechaInicio || fechaFin || searchQuery) && (
                    <button
                      className='btn btn-light-danger btn-icon'
                      onClick={() => {
                        setFechaInicio('')
                        setFechaFin('')
                        setSearchQuery('')
                        setPage(1)
                      }}
                      title='Limpiar filtros'
                    >
                      <KTIcon iconName='cross' className='fs-2' />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tabla */}
          {error ? (
            <div className='alert alert-danger d-flex align-items-center p-5'>
              <KTIcon iconName='information-5' className='fs-2hx text-danger me-4' />
              <div className='d-flex flex-column'>
                <h4 className='mb-1 text-danger'>Error al cargar movimientos</h4>
                <span>{error}</span>
              </div>
            </div>
          ) : (
            <MovimientosTable
              movimientos={movimientos}
              loading={loading}
              onEditPago={(pago) => setPagoParaEditar(pago)}
              onDeletePago={(idPago) => setIdPagoParaEliminar(idPago)}
            />
          )}

          {/* Paginación */}
          {totalPages > 1 && (
            <div className='d-flex flex-stack flex-wrap pt-6'>
              <div className='fs-6 fw-semibold text-gray-700'>
                Página {page} de {totalPages} - Total: {total} registros
              </div>
              <ul className='pagination'>
                <li className={`page-item previous ${page === 1 ? 'disabled' : ''}`}>
                  <button
                    className='page-link'
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                  >
                    <i className='previous'></i>
                  </button>
                </li>
                {[...Array(totalPages)].map((_, idx) => {
                  const pageNum = idx + 1
                  // Mostrar solo páginas cercanas a la actual
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= page - 2 && pageNum <= page + 2)
                  ) {
                    return (
                      <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                        <button
                          className='page-link'
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      </li>
                    )
                  } else if (pageNum === page - 3 || pageNum === page + 3) {
                    return <li key={pageNum} className='page-item disabled'><span className='page-link'>...</span></li>
                  }
                  return null
                })}
                <li className={`page-item next ${page === totalPages ? 'disabled' : ''}`}>
                  <button
                    className='page-link'
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                  >
                    <i className='next'></i>
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      <EditarPagoModal
        isOpen={!!pagoParaEditar}
        onClose={() => setPagoParaEditar(null)}
        pago={pagoParaEditar}
        onSuccess={handleRefreshMovimientos}
      />
      <ConfirmarEliminarPagoModal
        isOpen={!!idPagoParaEliminar}
        onClose={() => setIdPagoParaEliminar(null)}
        idPago={idPagoParaEliminar}
        onSuccess={handleRefreshMovimientos}
      />
    </>
  )
}

export default MovimientosPage
